import { pickBy, isEqual } from 'lodash';
import { useMemo } from 'react';
import { useSolvSelector } from '~/reducers';
import { useAutofillPaperwork } from '../hooks/useAutofillPaperwork';
import {
  PAYMENT_FORM_FIELD_NAMES,
  SECONDARY_PAYMENT_FORM_FIELD_NAMES,
} from '../paperworkConstants';

/**
 * Returns analytics about autofilling paperwork:
 *
 * {
 *  //Percentage of values that were autofilled that were actually used (i.e. unchanged by Mary)
 *  percentageOfAutofilledValuesUsed: float,
 *
 *  //Percentage of paperwork responses that were autofilled
 *  percentageOfFieldsAutofilled: float,
 *
 *  //If photo id was autofilled
 *  didAutofillId: boolean,
 *
 *  //If autofilled photo id was used (not switched out)
 *  usedAutofilledId: boolean,
 *
 *  //Total number of paperwork responses submitted, excluding those that are not returned in the autofill
 *  //endpoint, like custom questions and insurance
 *  totalSubmittingValues: number,
 * }
 */
export const useAutofillPaperworkStats = (bookingId: string) => {
  const autofillData = useAutofillPaperwork(bookingId);
  const paperwork = useSolvSelector((state) => state.paperwork);
  const photoId = useSolvSelector((state) => state.photoId);
  const { customFields, formData: completedFields } = paperwork;

  return useMemo(() => {
    const currentPhotoIdFrontId = photoId?.front?.id;
    const currentPhotoIdBackId = photoId?.back?.id;

    const paperworkResponses = autofillData?.paperworkResponses ?? {};
    const photoIdFrontId = autofillData?.photoIdFront?.id ?? '';
    const photoIdBackId = autofillData?.photoIdBack?.id ?? '';
    const customFieldNames = (customFields ?? []).map((field) => field.field_name);

    /**
     * Fields to exclude from the count of total paperwork fields we're submitting.
     * Exclude custom fields and payment fields from because they are not included
     * in the autofillable values from the autofill endpoint
     */
    const excludeFields = customFieldNames
      .concat(PAYMENT_FORM_FIELD_NAMES)
      .concat(SECONDARY_PAYMENT_FORM_FIELD_NAMES);

    const completedStandardFields = pickBy(completedFields, (v, k) => !excludeFields.includes(k));
    const totalFields = Object.keys(completedStandardFields).length;
    const totalAutofillFields = Object.keys(paperworkResponses).length;

    /**
     * Find which of the completed fields kept their autofilled values
     */
    const fieldsThatKeptAutofillValues = Object.keys(
      pickBy(paperworkResponses, (v, k) => isEqual(completedStandardFields[k], v))
    ).length;
    const percentageOfAutofilledValuesUsed = fieldsThatKeptAutofillValues / totalAutofillFields;
    const percentageOfFieldsAutofilled = totalAutofillFields / totalFields;

    /**
     * Check if Photo Id was autofilled and if it was kept the same or replaced
     */
    const didAutofillId = !!(photoIdFrontId || photoIdBackId);
    const usedAutofilledId = currentPhotoIdFrontId
      ? currentPhotoIdFrontId === photoIdFrontId || currentPhotoIdBackId === photoIdBackId
      : false;

    return {
      percentageOfAutofilledValuesUsed,
      percentageOfFieldsAutofilled,
      didAutofillId,
      usedAutofilledId,
      totalSubmittingValues: totalFields,
    };
  }, [autofillData, photoId, completedFields, customFields]);
};
